import { Helmet } from "react-helmet-async";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { brand } from "@/lib/brand";

export default function Seo({ title, description, image, type = "website", path, jsonLd }) {
  const { data: seo } = useQuery({
    queryKey: ["seo-settings"],
    queryFn: async () => (await api.get("/seo-settings")).data,
    staleTime: 5 * 60 * 1000,
  });

  const siteName = seo?.site_name || brand.name;
  const fullTitle = title
    ? (title.includes(siteName) ? title : `${title} | ${siteName}`)
    : seo?.default_title || siteName;
  const desc = description || seo?.default_description || "";
  const img = image || seo?.default_og_image ||
    "https://images.unsplash.com/photo-1579116317002-fd0cedb52425?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
  const origin = typeof window !== "undefined" ? window.location.origin : "https://www.holamovers.com";
  const url = typeof window !== "undefined" ? (path ? `${origin}${path}` : window.location.href) : "";

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: siteName,
    url: origin,
    description: seo?.default_description || desc,
    areaServed: "Mérida, Yucatán, Mexico",
    knowsAbout: ["Relocating to Mexico", "Moving to Mérida", "US to Mexico relocation"],
  };
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: siteName,
    url: origin,
  };
  const extra = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {seo?.keywords && <meta name="keywords" content={seo.keywords} />}
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:type" content={type} />
      {img && <meta property="og:image" content={img} />}
      {url && <meta property="og:url" content={url} />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      {img && <meta name="twitter:image" content={img} />}
      {url && <link rel="canonical" href={url} />}
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      {extra.map((schema, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(schema)}</script>
      ))}
    </Helmet>
  );
}
